import React, { useEffect, useMemo, useState } from "react";

/**
 * PT‑BR Advanced Storytelling Trainer
 * Focus: Narrative construction with past tenses, connectors, and story flow
 * Single-file React component. Tailwind-friendly, no external deps.
 */

interface StorySegment {
  id: string;
  prompt_en: string;
  context_pt: string;
  target_pt: string;
  tense_focus: "perfeito" | "imperfeito" | "mixed";
  story_function: "setup" | "action" | "description" | "dialogue" | "conclusion";
  connectors: string[];
  explain_pt: string;
  explain_en: string;
  writing_tip: string;
  alternatives: string[];
}

interface IconProps {
  className?: string;
  [key: string]: any;
}

// ——— Inline Icons ———
const IconWand = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M15 4l5 5M3 21l9-9"/>
    <path d="M8 3h2M3 8h2M13 8h2M8 13h2" strokeLinecap="round"/>
  </svg>
);
const IconBook = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);
const IconPlay = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <polygon points="6,4 20,12 6,20" fill="currentColor"/>
  </svg>
);
const IconCopy = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <rect x="9" y="9" width="13" height="13" rx="2"/>
    <rect x="2" y="2" width="13" height="13" rx="2"/>
  </svg>
);
const IconEye = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconDownload = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M12 3v12"/>
    <path d="M7 10l5 5 5-5"/>
    <rect x="3" y="18" width="18" height="3" rx="1.5"/>
  </svg>
);
const IconRefresh = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
    <path d="M21 3v5h-5"/>
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
    <path d="M3 21v-5h5"/>
  </svg>
);

// ——— Helper Functions ———
const stripDiacritics = (s: string) => s
  .normalize("NFD")
  .replace(/\p{Diacritic}/gu, "")
  .replace(/[^\p{L}\p{N}\s\-']/gu, "");

const normalize = (s: string) => stripDiacritics(String(s || "").toLowerCase().trim())
  .replace(/\s+/g, " ");

function lev(a: string, b: string) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[m][n];
}

function isCloseMatch(user: string, target: string) {
  const u = normalize(user);
  const t = normalize(target);
  if (!u) return false;
  if (u === t) return true;
  const d = lev(u, t);
  const tol = Math.max(1, Math.floor(t.length * 0.2)); // More lenient for longer stories
  return d <= tol;
}

// ——— Component ———
export default function StorytellingTrainerApp() {
  // Controls
  const [storyType, setStoryType] = useState("adventure"); // "adventure" | "childhood" | "travel" | "mystery" | "custom"
  const [storyLength, setStoryLength] = useState("medium"); // "short" | "medium" | "long"
  const [level, setLevel] = useState("B1");
  const [register, setRegister] = useState("informal");
  const [temperature, setTemperature] = useState(0.7);
  const [mockMode, setMockMode] = useState(false);
  const [customTheme, setCustomTheme] = useState("");

  // Story state
  const [segments, setSegments] = useState<StorySegment[]>([]);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [userStory, setUserStory] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hasClaude = true; // Always available via API

  // Persistence
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("story_trainer_prefs") || "null");
      if (saved) {
        setStoryType(saved.storyType ?? "adventure");
        setStoryLength(saved.storyLength ?? "medium");
        setLevel(saved.level ?? "B1");
        setRegister(saved.register ?? "informal");
        setTemperature(saved.temperature ?? 0.7);
        setCustomTheme(saved.customTheme ?? "");
      }
      const savedSegments = JSON.parse(localStorage.getItem("story_segments") || "null");
      if (savedSegments) setSegments(savedSegments);
      const savedUserStory = JSON.parse(localStorage.getItem("user_story") || "null");
      if (savedUserStory) setUserStory(savedUserStory);
    } catch {}
  }, []);

  useEffect(() => {
    const prefs = { storyType, storyLength, level, register, temperature, customTheme };
    localStorage.setItem("story_trainer_prefs", JSON.stringify(prefs));
    localStorage.setItem("story_segments", JSON.stringify(segments));
    localStorage.setItem("user_story", JSON.stringify(userStory));
  }, [storyType, storyLength, level, register, temperature, customTheme, segments, userStory]);

  // System prompt for story generation
  const systemPrompt = useMemo(() => `You are a PT-BR storytelling teacher. Create guided storytelling exercises.

Return ONLY strict JSON (no markdown fences) with:
{
  "segments": [
    {
      "id": string,                    // unique identifier
      "prompt_en": string,             // instruction in English for the learner
      "context_pt": string,            // story context so far in Portuguese
      "target_pt": string,             // example response in Portuguese
      "tense_focus": "perfeito"|"imperfeito"|"mixed",
      "story_function": "setup"|"action"|"description"|"dialogue"|"conclusion",
      "connectors": string[],          // suggested connectors: ["então", "depois", "enquanto"]
      "explain_pt": string,            // why this tense/structure works here
      "explain_en": string,            // English explanation
      "writing_tip": string,           // narrative technique tip
      "alternatives": string[]         // 2-3 alternative ways to express it
    }
  ]
}

Guidelines:
- Create ${storyLength === "short" ? "4-5" : storyLength === "medium" ? "6-8" : "10-12"} interconnected story segments
- Theme: ${storyType === "custom" ? customTheme : storyType}
- Use natural Brazilian Portuguese, CEFR level ${level}, ${register} register
- Mix perfeito (completed actions) and imperfeito (descriptions/habits/ongoing actions)
- Include narrative connectors (então, depois, enquanto, de repente, naquela época)
- Progress from setup → development → climax → resolution
- Each segment should build on the previous context
- Vary sentence structures and story functions
- Include dialogue opportunities where natural

Story structure:
- Setup: Set scene with imperfeito (era, tinha, morava)
- Action: Key events with perfeito (foi, fez, disse, aconteceu)
- Description: Background details with imperfeito (estava, parecia, sentia)
- Conclusion: Resolution with perfeito and reflection`, [storyLength, storyType, customTheme, level, register]);

  function buildUserPrompt() {
    return `Create a ${storyLength} ${storyType === "custom" ? customTheme : storyType} story progression. Level: ${level}. Register: ${register}.`;
  }

  async function generateStorySegments() {
    setLoading(true);
    setError("");
    setSegments([]);
    setUserStory([]);
    setCurrentSegment(0);
    
    try {
      let respText = "";
      if (!mockMode && hasClaude) {
        const apiUrl = '/api/ai/storytelling-trainer';
        console.log('🌍 API URL:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            system: systemPrompt,
            messages: [{ role: "user", content: buildUserPrompt() }],
            temperature,
            max_tokens: 2000
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          respText = data.response;
        } else {
          throw new Error(data.error || 'Failed to get AI response');
        }
      } else {
        // Mock data
        respText = JSON.stringify(getMockStoryData());
        await new Promise(r => setTimeout(r, 800));
      }

      const data = parseJSONSafe(respText);
      if (data?.segments && Array.isArray(data.segments)) {
        setSegments(data.segments);
        setUserStory(new Array(data.segments.length).fill(""));
      } else {
        throw new Error("No story segments in response");
      }
    } catch (e) {
      console.error('AI request error:', e);
      setError('Failed to connect to AI. Using mock story.');
      const mockData = getMockStoryData();
      setSegments(mockData.segments);
      setUserStory(new Array(mockData.segments.length).fill(""));
    } finally {
      setLoading(false);
    }
  }

  function getMockStoryData() {
    return {
      segments: [
        {
          id: "setup1",
          prompt_en: "Set the scene: Where and when did this story take place? What was the atmosphere like?",
          context_pt: "",
          target_pt: "Era uma manhã de domingo ensolarada. Eu tinha dez anos e morava com meus avós numa casa pequena no interior.",
          tense_focus: "imperfeito" as const,
          story_function: "setup" as const,
          connectors: ["Era", "Naquela época", "Quando eu era criança"],
          explain_pt: "Imperfeito descreve o cenário e situação contínua no passado.",
          explain_en: "Imperfeito describes ongoing background and scene setting.",
          writing_tip: "Use imperfeito to paint the picture and set the mood.",
          alternatives: ["Naquele domingo...", "Quando eu tinha dez anos...", "Em casa dos meus avós..."]
        },
        {
          id: "action1",
          prompt_en: "What specific event started the adventure? What happened suddenly?",
          context_pt: "Era uma manhã de domingo ensolarada. Eu tinha dez anos e morava com meus avós numa casa pequena no interior.",
          target_pt: "De repente, ouvi um barulho estranho vindo do sótão. Decidi subir para investigar.",
          tense_focus: "mixed" as const,
          story_function: "action" as const,
          connectors: ["De repente", "Então", "Naquele momento"],
          explain_pt: "Perfeito para a ação específica (ouvi, decidi). Imperfeito seria para ação contínua.",
          explain_en: "Perfeito for specific completed actions. Use to move the story forward.",
          writing_tip: "Mix sudden events (perfeito) with ongoing descriptions (imperfeito).",
          alternatives: ["Subitamente escutei...", "Foi quando percebi...", "Algo me chamou atenção..."]
        }
      ]
    };
  }

  function parseJSONSafe(text: string) {
    try {
      const first = text.indexOf("{");
      const last = text.lastIndexOf("}");
      const body = first >= 0 && last > first ? text.slice(first, last + 1) : text;
      return JSON.parse(body);
    } catch { return null; }
  }

  // ——— Story Navigation ———
  function updateUserStorySegment(index: number, text: string) {
    const newStory = [...userStory];
    newStory[index] = text;
    setUserStory(newStory);
  }

  function nextSegment() {
    if (currentSegment < segments.length - 1) {
      setCurrentSegment(currentSegment + 1);
    }
  }

  function prevSegment() {
    if (currentSegment > 0) {
      setCurrentSegment(currentSegment - 1);
    }
  }

  function resetStory() {
    setUserStory(new Array(segments.length).fill(""));
    setCurrentSegment(0);
  }

  // ——— Utilities ———
  function speak(text: string, lang = "pt-BR") {
    try { 
      const u = new SpeechSynthesisUtterance(text); 
      u.lang = lang; 
      window.speechSynthesis.cancel(); 
      window.speechSynthesis.speak(u); 
    } catch {}
  }

  function copy(text: string) { 
    navigator.clipboard?.writeText(text); 
  }

  function exportStory() {
    const fullStory = userStory.filter(Boolean).join(" ");
    const storyData = {
      theme: storyType,
      level,
      register,
      segments: segments.map((seg, i) => ({
        prompt: seg.prompt_en,
        user_text: userStory[i] || "",
        target_text: seg.target_pt,
        function: seg.story_function
      })),
      full_story: fullStory
    };
    
    const blob = new Blob([JSON.stringify(storyData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "minha_historia_ptbr.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  // ——— Current Segment Component ———
  function CurrentSegmentCard() {
    if (segments.length === 0) return null;
    
    const segment = segments[currentSegment];
    const userText = userStory[currentSegment] || "";
    const isGoodMatch = isCloseMatch(userText, segment.target_pt);
    const [revealed, setRevealed] = useState(false);

    return (
      <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500">
              <span>{segment.story_function}</span>
              <span>•</span>
              <span>{segment.tense_focus}</span>
              <span>•</span>
              <span>{currentSegment + 1} / {segments.length}</span>
            </div>
            <h3 className="text-lg font-semibold mt-1">{segment.prompt_en}</h3>
          </div>
          <IconBook className="w-6 h-6 text-neutral-400"/>
        </div>

        {segment.context_pt && (
          <div className="rounded-xl bg-neutral-50 p-3">
            <div className="text-xs font-medium text-neutral-600 mb-1">Story so far:</div>
            <div className="text-sm text-neutral-700">{segment.context_pt}</div>
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">Continue the story:</label>
          <textarea
            className={`w-full min-h-[120px] rounded-xl border px-4 py-3 outline-none focus:ring-2 resize-y ${
              isGoodMatch ? 'border-emerald-400 ring-emerald-200' : 'focus:ring-neutral-800'
            }`}
            placeholder="Type your part of the story in Portuguese..."
            value={userText}
            onChange={(e) => updateUserStorySegment(currentSegment, e.target.value)}
          />
        </div>

        {segment.connectors.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <span className="text-xs font-medium text-neutral-600">Suggested connectors:</span>
            {segment.connectors.map((conn, i) => (
              <button
                key={i}
                onClick={() => updateUserStorySegment(currentSegment, userText + (userText ? " " : "") + conn + " ")}
                className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100"
              >
                {conn}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={() => setRevealed(!revealed)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border hover:bg-neutral-50"
          >
            <IconEye className="w-4 h-4"/>
            {revealed ? 'Hide' : 'Show'} example
          </button>
          <button
            onClick={() => speak(segment.target_pt)}
            className="p-2 rounded-xl border hover:bg-neutral-50"
            title="Play example"
          >
            <IconPlay className="w-4 h-4"/>
          </button>
          {userText && (
            <button
              onClick={() => speak(userText)}
              className="p-2 rounded-xl border hover:bg-neutral-50"
              title="Play your text"
            >
              <IconPlay className="w-4 h-4 text-blue-600"/>
            </button>
          )}
        </div>

        {revealed && (
          <div className="rounded-xl border bg-neutral-50 p-4 space-y-2">
            <div className="text-sm">
              <div className="text-xs text-neutral-600 mb-1">Example response:</div>
              <div className="font-medium mb-2">{segment.target_pt}</div>
              <div className="text-xs text-neutral-700">
                <span className="font-medium">PT:</span> {segment.explain_pt}
              </div>
              <div className="text-xs text-neutral-700">
                <span className="font-medium">EN:</span> {segment.explain_en}
              </div>
              <div className="text-xs text-blue-700 mt-1">
                <span className="font-medium">Tip:</span> {segment.writing_tip}
              </div>
            </div>
            
            {segment.alternatives.length > 0 && (
              <div className="text-xs border-t pt-2">
                <span className="font-medium text-neutral-600">Alternatives:</span>
                <div className="mt-1 space-x-1">
                  {segment.alternatives.map((alt, i) => (
                    <span key={i} className="inline-block bg-neutral-100 rounded px-2 py-1">
                      {alt}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t">
          <button
            onClick={prevSegment}
            disabled={currentSegment === 0}
            className="px-4 py-2 rounded-xl border disabled:opacity-50"
          >
            Previous
          </button>
          
          <div className="text-xs text-neutral-500">
            {isGoodMatch && userText ? "✓ Good match!" : userText ? "Keep writing..." : "Start writing..."}
          </div>
          
          <button
            onClick={nextSegment}
            disabled={currentSegment === segments.length - 1}
            className="px-4 py-2 rounded-xl bg-neutral-900 text-white disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              PT‑BR Storytelling Trainer — Advanced Narrative Practice
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Write stories segment by segment. Master past tenses and narrative flow in context.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!hasClaude && !mockMode && (
              <span className="rounded-full bg-amber-100 text-amber-900 px-3 py-1 text-xs">
                Connect Claude to go live
              </span>
            )}
            <label className="flex items-center gap-2 text-xs select-none">
              <input 
                type="checkbox" 
                className="scale-110" 
                checked={mockMode} 
                onChange={(e) => setMockMode(e.target.checked)} 
              />
              Mock mode
            </label>
          </div>
        </header>

        {/* Configuration Panel */}
        <section className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <h3 className="text-sm font-semibold mb-4">Story Settings</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-600">Theme</label>
                <select 
                  className="mt-1 w-full rounded-xl border px-3 py-2" 
                  value={storyType} 
                  onChange={(e) => setStoryType(e.target.value)}
                >
                  <option value="adventure">Adventure</option>
                  <option value="childhood">Childhood Memory</option>
                  <option value="travel">Travel Experience</option>
                  <option value="mystery">Mystery</option>
                  <option value="romance">Romance</option>
                  <option value="family">Family Story</option>
                  <option value="custom">Custom Theme</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600">Length</label>
                <select 
                  className="mt-1 w-full rounded-xl border px-3 py-2" 
                  value={storyLength} 
                  onChange={(e) => setStoryLength(e.target.value)}
                >
                  <option value="short">Short (4-5 segments)</option>
                  <option value="medium">Medium (6-8 segments)</option>
                  <option value="long">Long (10-12 segments)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600">Level</label>
                <select 
                  className="mt-1 w-full rounded-xl border px-3 py-2" 
                  value={level} 
                  onChange={(e) => setLevel(e.target.value)}
                >
                  {["A2", "B1", "B2", "C1"].map(l => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600">Register</label>
                <select 
                  className="mt-1 w-full rounded-xl border px-3 py-2" 
                  value={register} 
                  onChange={(e) => setRegister(e.target.value)}
                >
                  {["informal", "neutral", "formal"].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            </div>

            {storyType === "custom" && (
              <div className="mt-3">
                <label className="block text-xs font-medium text-neutral-600">Custom Theme</label>
                <input
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  placeholder="e.g., First day at work, Moving to a new city..."
                  value={customTheme}
                  onChange={(e) => setCustomTheme(e.target.value)}
                />
              </div>
            )}

            <div className="mt-4">
              <label className="block text-xs font-medium text-neutral-600">
                Creativity: {temperature.toFixed(2)}
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <h3 className="text-sm font-semibold mb-4">Story Progress</h3>
            {segments.length > 0 && (
              <div className="space-y-2">
                {segments.map((seg, i) => (
                  <div
                    key={seg.id}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${
                      i === currentSegment ? 'bg-blue-50 border border-blue-200' : 
                      userStory[i] ? 'bg-green-50' : 'bg-neutral-50'
                    }`}
                    onClick={() => setCurrentSegment(i)}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      i === currentSegment ? 'bg-blue-600 text-white' :
                      userStory[i] ? 'bg-green-600 text-white' : 'bg-neutral-300 text-neutral-600'
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 text-xs">
                      <div className="font-medium">{seg.story_function}</div>
                      <div className="text-neutral-500 truncate">{seg.prompt_en}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {segments.length === 0 && (
              <div className="text-center text-neutral-500 py-8">
                <IconBook className="w-12 h-12 mx-auto mb-2 text-neutral-300"/>
                <p className="text-sm">Generate a story to start writing</p>
              </div>
            )}
          </div>
        </section>

        {/* Action Buttons */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={generateStorySegments}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 bg-neutral-900 text-white disabled:opacity-60"
          >
            <IconWand className="w-4 h-4"/>
            {loading ? "Generating..." : "Generate Story"}
          </button>
          
          {segments.length > 0 && (
            <>
              <button
                onClick={resetStory}
                className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 border"
              >
                <IconRefresh className="w-4 h-4"/>
                Reset Writing
              </button>
              
              <button
                onClick={exportStory}
                disabled={!userStory.some(Boolean)}
                className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 border disabled:opacity-50"
              >
                <IconDownload className="w-4 h-4"/>
                Export Story
              </button>
            </>
          )}
          
          <span className="text-xs text-neutral-500">
            Write naturally, then reveal examples to learn narrative techniques.
          </span>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {/* Main Story Interface */}
        {segments.length > 0 && (
          <section className="mt-6">
            <CurrentSegmentCard />
          </section>
        )}

        {/* Complete Story Preview */}
        {userStory.some(Boolean) && (
          <section className="mt-6 bg-white rounded-2xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4">Your Complete Story</h3>
            <div className="rounded-xl bg-neutral-50 p-4">
              <div className="space-y-2 text-sm text-neutral-700 leading-relaxed">
                {userStory.map((text, i) => (
                  text && (
                    <span key={i} className="inline">
                      {text}{i < userStory.length - 1 ? " " : ""}
                    </span>
                  )
                ))}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => speak(userStory.filter(Boolean).join(" "))}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border hover:bg-neutral-50"
              >
                <IconPlay className="w-4 h-4"/>
                Play Full Story
              </button>
              <button
                onClick={() => copy(userStory.filter(Boolean).join(" "))}
                className="p-2 rounded-xl border hover:bg-neutral-50"
                title="Copy story"
              >
                <IconCopy className="w-4 h-4"/>
              </button>
            </div>
          </section>
        )}

        <footer className="mt-10 text-center text-[11px] text-neutral-500">
          Advanced storytelling practice for Brazilian Portuguese learners. 
          Mix perfeito and imperfeito naturally in narrative context.
        </footer>
      </div>
    </div>
  );
}